import { Divider, Group } from '@mantine/core';

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useTracking } from '../../hooks/useTracking';
import { useInvitationStatus } from '../../hooks/useInvitationStatus';
import {
  InvitedSupplier,
  UserInvitation as UserInvitationType,
  UserInvitationStatus,
} from '../../shared/types';
import { ActionIcon, Button, Flex, Stack, Text } from '@mantine/core';
import { Icon } from '../../../lib/components';
import moment from 'moment';
import { useUserInvitations } from '../../hooks/useUserInvitations';
import classes from './Notifications.module.css';

enum InvitationUIStatus {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

type UserInvitationProps = {
  id: string;
  inviterEmail: string;
  inviterName: string;
  createdAt: string;
  businessName: string;
};

const UserInvitation = ({
  id,
  inviterEmail,
  inviterName,
  createdAt,
  businessName,
}: UserInvitationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const { status } = useInvitationStatus();
  const { updateUserInvitation } = useUserInvitations();
  const [invitationStatus, setInvitationStatus] = useState<InvitationUIStatus>(
    InvitationUIStatus.PENDING
  );

  const handleAccept = async () => {
    try {
      await updateUserInvitation.mutateAsync({
        id,
        update: { status: UserInvitationStatus.ACCEPTED },
      });
      setInvitationStatus(InvitationUIStatus.ACCEPTED);
      trackEvent('Notifications', {
        step: 'user-connection-received',
        action: 'accept',
      });
    } catch (e: unknown) {
      setInvitationStatus(InvitationUIStatus.FAILED);
      console.log(
        'error updating invitation',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleDecline = async () => {
    try {
      await updateUserInvitation.mutateAsync({
        id,
        update: { status: UserInvitationStatus.REJECTED },
      });
      setInvitationStatus(InvitationUIStatus.REJECTED);
      trackEvent('Notifications', {
        step: 'user-connection-received',
        action: 'reject',
      });
    } catch (e: unknown) {
      setInvitationStatus(InvitationUIStatus.FAILED);
      console.log(
        'error updating invitation',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const cardText = (
    <>
      <b>{inviterName}</b>{' '}
      {t('notifications.userInvitation.invite', {
        businessName,
      })}
    </>
  );

  if (invitationStatus === InvitationUIStatus.ACCEPTED) {
    return (
      <Flex align="center" justify="space-between" p={15} h={135}>
        <Text>
          <Trans
            i18nKey="notifications.userInvitation.accepted"
            values={{ inviterName }}
            components={{ b: <b /> }}
          />
        </Text>
        <ActionIcon
          onClick={() => {
            queryClient.setQueryData(
              ['user-invitations', status],
              (prevInvites: UserInvitationType[] | undefined) =>
                prevInvites?.filter(
                  (invite: UserInvitationType) => invite.id !== id
                )
            );
          }}
        >
          <Icon icon="xmark" />
        </ActionIcon>
      </Flex>
    );
  }

  if (invitationStatus === InvitationUIStatus.REJECTED) {
    return (
      <Flex align="center" justify="space-between" p={15} h={135}>
        <Text>
          <Trans
            i18nKey="notifications.userInvitation.declined"
            values={{ inviterName }}
            components={{ b: <b /> }}
          />
        </Text>
        <ActionIcon
          onClick={() => {
            queryClient.setQueryData(
              ['business-invitations', status],
              (prevInvites: InvitedSupplier[] | undefined) =>
                prevInvites?.filter(
                  (invite: InvitedSupplier) => invite.id !== id
                )
            );
          }}
        >
          <Icon icon="xmark" />
        </ActionIcon>
      </Flex>
    );
  }

  if (invitationStatus === InvitationUIStatus.FAILED) {
    return (
      <Flex align="center" justify="space-between" p={15} h={135}>
        <Text>{cardText}</Text>
        <Button
          variant="outline"
          className={classes.button}
          onClick={handleAccept}
        >
          {t('common.retry')}
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Stack gap={5} p={15} h={135}>
        <Text mb={10}>{cardText}</Text>
        <Stack gap="sm">
          <Text size="xs">{moment(createdAt).fromNow()}</Text>
          <Group gap={10}>
            <Button
              w={100}
              variant="outline"
              className={classes.button}
              onClick={handleDecline}
            >
              {t('common.decline')}
            </Button>
            <Button w={100} onClick={handleAccept}>
              {t('common.accept')}
            </Button>
          </Group>
        </Stack>
      </Stack>
      <Divider />
    </>
  );
};

export default UserInvitation;
